import { useCallback } from "react";
import { useMotionValue, useReducedMotion, useSpring } from "framer-motion";

/**
 * Pointer parallax for the auth showcase.
 *
 * The handler is attached to the showcase `<aside>` rather than to the
 * floating-card layer itself: that layer is `pointer-events-none` so the
 * cards can never intercept a click, and an element that can't be a pointer
 * target never receives `pointermove` — so the handler would never fire
 * there. Listening on the panel root keeps the cards click-through *and*
 * keeps the depth.
 *
 * Motion is normalised against the viewport rather than the panel so the
 * lean is the same whether the cursor is over the showcase or over the form.
 *
 * `reset` returns the stack to neutral. Without it the cards would freeze
 * at whatever offset they last reached and stay visibly off-axis for the
 * rest of the visit.
 */
export function usePointerParallax() {
  const reduceMotion = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const smoothX = useSpring(x, { stiffness: 55, damping: 16, mass: 0.7 });
  const smoothY = useSpring(y, { stiffness: 55, damping: 16, mass: 0.7 });

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (reduceMotion) return;
      x.set((event.clientX / window.innerWidth - 0.5) * -26);
      y.set((event.clientY / window.innerHeight - 0.5) * -18);
    },
    [reduceMotion, x, y]
  );

  const reset = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return { smoothX, smoothY, onPointerMove, reset };
}